/** Sort presets, week options and the past-week rule for the shared list
 *  control (spec docs/superpowers/specs/2026-09-26-shared-list-filters-design.md). */
import { comparePosition } from './refereeCrew'
import { isRefPosition, positionLabelFor, positionLabelRank } from './refereePositions'
import { compareNatural } from './naturalOrder'
import { compareDivision } from './divisionOrder'
import type { SortPresetId } from './listFilters'

export type SortKey = 'date' | 'time' | 'division' | 'field' | 'role' | 'activity' | 'team' | 'week'

export interface SortableRow {
  date: string | null
  time: string
  division: string
  field_name: string | null
  /** Slot rows only. A row without one ties on `role`. */
  position?: string
  /** ISO instant of the row's latest activity; null when nothing has happened.
   *  A row without the field ties on `activity`. */
  activity_at?: string | null
  /** The row's team text (a matchup). A row without one ties on `team`. */
  team?: string
  /** The season week; null for a row with no game yet. A row without the
   *  field ties on `week`. */
  week?: number | null
}

export const SORT_PRESETS: Record<SortPresetId, { label: string; keys: readonly SortKey[] }> = {
  datetime: { label: 'Date & time', keys: ['date', 'time', 'field'] },
  division: { label: 'Division', keys: ['division', 'date', 'time'] },
  field: { label: 'Field', keys: ['field', 'date', 'time'] },
  time: { label: 'Kickoff time', keys: ['time', 'date', 'field'] },
  role: { label: 'Role', keys: ['role', 'date', 'time', 'field'] },
  activity: { label: 'Recent activity', keys: ['activity', 'date', 'time', 'field'] },
  team: { label: 'Team', keys: ['team', 'date', 'time'] },
  // The weekend first, so a division or field sort never splits a weekend
  // apart, and an unscheduled game stays in its own week.
  'week-division': { label: 'Weekend, then division', keys: ['week', 'division', 'date', 'time'] },
  'week-field': { label: 'Weekend, then field', keys: ['week', 'field', 'date', 'time'] },
}

const plain = (a: string, b: string): number => (a === b ? 0 : a < b ? -1 : 1)

/** Rank by the label a card shows (an 8U AR2 reads "Referee", so it sorts with
 *  the Referees, as the Role facet groups it), then by crew order. */
function compareRole(a: SortableRow, b: SortableRow): number {
  if (a.position === undefined || b.position === undefined) return 0
  const labelRank = (division: string, position: string) =>
    positionLabelRank(isRefPosition(position) ? positionLabelFor(division, position) : position)
  return labelRank(a.division, a.position) - labelRank(b.division, b.position)
    || comparePosition({ position: a.position }, { position: b.position })
}

const KEY_COMPARE: Record<SortKey, (a: SortableRow, b: SortableRow) => number> = {
  // ⚠️ Undated FIRST: where SQLite puts a NULL on an ascending ORDER BY, and so
  // where `listOpenSlots` and `listSeasonSlots` already put it.
  date: (a, b) => (a.date === b.date ? 0 : a.date === null ? -1 : b.date === null ? 1 : plain(a.date, b.date)),
  time: (a, b) => plain(a.time, b.time),
  // One division order for every list (owner, 2026-09-26): oldest age first, G before B.
  division: (a, b) => compareDivision(a.division, b.division),
  // A game with no field yet reads "Field TBD" and sorts after named fields.
  field: (a, b) =>
    a.field_name === b.field_name ? 0 : a.field_name === null ? 1 : b.field_name === null ? -1 : compareNatural(a.field_name, b.field_name),
  role: compareRole,
  // Newest first, the one descending key: "what changed lately" is the
  // question. ISO instants compare as strings. Never-touched rows go last.
  activity: (a, b) => {
    if (a.activity_at === undefined || b.activity_at === undefined) return 0
    if (a.activity_at === b.activity_at) return 0
    if (a.activity_at === null) return 1
    if (b.activity_at === null) return -1
    return plain(b.activity_at, a.activity_at)
  },
  team: (a, b) => (a.team === undefined || b.team === undefined ? 0 : compareNatural(a.team, b.team)),
  // A row with no game yet (a null week) sorts after every week.
  week: (a, b) => {
    if (a.week === undefined || b.week === undefined || a.week === b.week) return 0
    if (a.week === null) return 1
    if (b.week === null) return -1
    return a.week - b.week
  },
}

export function compareBy<Row extends SortableRow>(keys: readonly SortKey[]): (a: Row, b: Row) => number {
  return (a, b) => {
    for (const k of keys) {
      const c = KEY_COMPARE[k](a, b)
      if (c !== 0) return c
    }
    return 0
  }
}

/** PURE, stable: a copy sorted by `keys`. */
export function sortRows<Row extends SortableRow>(rows: readonly Row[], keys: readonly SortKey[]): Row[] {
  return [...rows].sort(compareBy<Row>(keys))
}

// ── Weeks ──────────────────────────────────────────────────────────────────

export interface WeekRow { week: number; date: string | null }
export interface WeekOption { week: number; date: string | null }

/** PURE. One option per week, dated by its EARLIEST dated row.
 *  ⚠️ Never derived from the week number: a week spans Saturday and Sunday. */
export function weekOptions(rows: readonly WeekRow[]): WeekOption[] {
  const earliest = new Map<number, string | null>()
  for (const r of rows) {
    const held = earliest.get(r.week)
    if (!earliest.has(r.week) || (r.date !== null && (held === null || held === undefined || r.date < held))) {
      earliest.set(r.week, r.date ?? held ?? null)
    }
  }
  return [...earliest.entries()].map(([week, date]) => ({ week, date })).sort((a, b) => a.week - b.week)
}

/** "Week 3 · Sat, Sep 27", or "Week 3" when the week has no dated row. The
 *  date is a wall-clock string, so it is read at noon UTC and printed in UTC:
 *  no browser timezone can move it a day. */
export function weekOptionLabel(option: WeekOption): string {
  if (!option.date) return `Week ${option.week}`
  const day = new Date(`${option.date}T12:00:00Z`).toLocaleDateString('en-US', {
    timeZone: 'UTC', weekday: 'short', month: 'short', day: 'numeric',
  })
  return `Week ${option.week} · ${day}`
}

/** PURE. Weeks whose LATEST dated row is before `today` (league YYYY-MM-DD).
 *  Per week, not per row: Saturday's games stay on screen through Sunday. */
export function pastWeeks(rows: readonly WeekRow[], today: string): Set<number> {
  const latest = new Map<number, string>()
  for (const r of rows) {
    if (r.date === null) continue
    const held = latest.get(r.week)
    if (held === undefined || r.date > held) latest.set(r.week, r.date)
  }
  return new Set([...latest].filter(([, d]) => d < today).map(([w]) => w))
}

/** PURE. Drop rows in past weeks. An undated row is never past. */
export function hidePastWeeks<Row extends WeekRow>(rows: readonly Row[], today: string): Row[] {
  const past = pastWeeks(rows, today)
  return rows.filter((r) => r.date === null || !past.has(r.week))
}
